import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  AlertTriangle, RefreshCw, Trash2, Search, 
  AlertCircle, XCircle, CheckCircle, Clock
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_ICONS = {
  400: <AlertCircle className="h-4 w-4 text-amber-500" />,
  401: <XCircle className="h-4 w-4 text-red-500" />,
  403: <XCircle className="h-4 w-4 text-red-500" />,
  404: <AlertCircle className="h-4 w-4 text-amber-500" />,
  500: <AlertTriangle className="h-4 w-4 text-red-600" />,
  502: <AlertTriangle className="h-4 w-4 text-red-600" />,
  503: <AlertTriangle className="h-4 w-4 text-red-600" />
};

const STATUS_COLORS = {
  400: 'bg-amber-50 border-amber-200',
  401: 'bg-red-50 border-red-200',
  403: 'bg-red-50 border-red-200',
  404: 'bg-amber-50 border-amber-200',
  500: 'bg-red-100 border-red-300',
  502: 'bg-red-100 border-red-300',
  503: 'bg-red-100 border-red-300'
};

export default function ErrorLog({ adminToken }) {
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({
    status_code: '',
    path_contains: '',
    limit: 100
  });
  const [selectedError, setSelectedError] = useState(null);

  const axiosConfig = {
    headers: { Authorization: `Bearer ${adminToken}` }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status_code) params.append('status_code', filters.status_code);
      if (filters.path_contains) params.append('path_contains', filters.path_contains);
      params.append('limit', filters.limit);

      const [errorsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/api/admin/errors?${params.toString()}`, axiosConfig),
        axios.get(`${API}/api/admin/errors/summary?hours=24`, axiosConfig)
      ]);

      setErrors(errorsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error('Failed to fetch error logs');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleClearOldErrors = async () => {
    if (!window.confirm('Clear errors older than 30 days?')) return;

    try {
      const response = await axios.delete(
        `${API}/api/admin/errors/clear?days_to_keep=30`,
        axiosConfig
      );
      toast.success(`Deleted ${response.data.deleted_count} old errors`);
      fetchData();
    } catch (error) {
      toast.error('Failed to clear old errors');
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString();
  };

  if (loading && !summary) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-zinc-900">Error Logs</h1>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="destructive" size="sm" onClick={handleClearOldErrors}>
            <Trash2 className="h-4 w-4 mr-2" />
            Clear Old
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-zinc-500">Total Errors (24h)</p>
                <p className="text-2xl font-bold text-zinc-900">{summary.total_errors}</p>
              </div>
            </div>
          </Card>

          {Object.entries(summary.by_status_code || {}).slice(0, 3).map(([code, count]) => (
            <Card key={code} className={`p-4 ${STATUS_COLORS[code] || 'bg-zinc-50'}`}>
              <div className="flex items-center gap-3">
                {STATUS_ICONS[code] || <AlertCircle className="h-5 w-5 text-zinc-500" />}
                <div>
                  <p className="text-sm text-zinc-500">HTTP {code}</p>
                  <p className="text-2xl font-bold text-zinc-900">{count}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Top Error Paths */}
      {summary?.top_error_paths?.length > 0 && (
        <Card className="p-4">
          <h3 className="text-sm font-semibold text-zinc-700 mb-3">Top Error Paths (24h)</h3>
          <div className="flex flex-wrap gap-2">
            {summary.top_error_paths.map((item, idx) => (
              <button
                key={idx}
                className="px-3 py-1 bg-red-50 border border-red-200 rounded-full text-sm text-red-700 hover:bg-red-100"
                onClick={() => setFilters({ ...filters, path_contains: item.path })}
              >
                {item.path} ({item.count})
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* Filters */}
      <Card className="p-4">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <Input
              placeholder="Filter by path..."
              value={filters.path_contains}
              onChange={(e) => setFilters({ ...filters, path_contains: e.target.value })}
              className="w-full"
            />
          </div>
          <Select
            value={filters.status_code || "all"}
            onValueChange={(val) => setFilters({ ...filters, status_code: val === "all" ? "" : val })}
          >
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Status code" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="400">400 Bad Request</SelectItem>
              <SelectItem value="401">401 Unauthorized</SelectItem>
              <SelectItem value="403">403 Forbidden</SelectItem>
              <SelectItem value="404">404 Not Found</SelectItem>
              <SelectItem value="500">500 Server Error</SelectItem>
              <SelectItem value="502">502 Bad Gateway</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={String(filters.limit)}
            onValueChange={(val) => setFilters({ ...filters, limit: parseInt(val) })}
          >
            <SelectTrigger className="w-28">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
              <SelectItem value="200">200</SelectItem>
              <SelectItem value="500">500</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={fetchData} disabled={loading}>
            <Search className="h-4 w-4 mr-2" />
            Search
          </Button>
        </div>
      </Card>

      {/* Error List */}
      <Card className="p-4">
        <div className="space-y-2 max-h-[600px] overflow-y-auto">
          {errors.length === 0 ? (
            <div className="text-center py-8 text-zinc-500">
              <CheckCircle className="h-12 w-12 mx-auto mb-2 text-emerald-500" />
              <p>No errors found!</p>
            </div>
          ) : (
            errors.map((error, idx) => (
              <div
                key={idx}
                className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                  STATUS_COLORS[error.status_code] || 'bg-zinc-50 border-zinc-200'
                } ${selectedError === idx ? 'ring-2 ring-indigo-500' : ''}`}
                onClick={() => setSelectedError(selectedError === idx ? null : idx)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    {STATUS_ICONS[error.status_code] || <AlertCircle className="h-4 w-4" />}
                    <span className="font-mono text-sm font-medium">
                      {error.method} {error.path}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                      error.status_code >= 500 ? 'bg-red-200 text-red-800' :
                      error.status_code >= 400 ? 'bg-amber-200 text-amber-800' :
                      'bg-zinc-200 text-zinc-800'
                    }`}>
                      {error.status_code}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-zinc-500">
                    <Clock className="h-3 w-3" />
                    {formatDate(error.timestamp)}
                  </div>
                </div>

                <p className="text-sm text-zinc-600 mt-1 line-clamp-1">{error.message}</p>

                {/* Expanded Details */}
                {selectedError === idx && (
                  <div className="mt-3 pt-3 border-t border-zinc-200 space-y-2">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-zinc-500">Error Type:</span>
                        <span className="ml-2 font-medium">{error.error_type}</span>
                      </div>
                      <div>
                        <span className="text-zinc-500">Has Auth:</span>
                        <span className="ml-2 font-medium">{error.has_auth ? 'Yes' : 'No'}</span>
                      </div>
                      <div>
                        <span className="text-zinc-500">IP Address:</span>
                        <span className="ml-2 font-mono">{error.ip_address || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-zinc-500">Query Params:</span>
                        <span className="ml-2 font-mono text-xs">{error.query_params || 'None'}</span>
                      </div>
                    </div>

                    {error.traceback && (
                      <div className="mt-2">
                        <p className="text-xs text-zinc-500 mb-1">Traceback:</p>
                        <pre className="bg-zinc-900 text-zinc-100 p-3 rounded text-xs overflow-x-auto max-h-40">
                          {error.traceback}
                        </pre>
                      </div>
                    )}

                    {error.request_body_preview && (
                      <div className="mt-2">
                        <p className="text-xs text-zinc-500 mb-1">Request Body Preview:</p>
                        <pre className="bg-zinc-100 p-2 rounded text-xs overflow-x-auto">
                          {error.request_body_preview}
                        </pre>
                      </div>
                    )}

                    <div className="text-xs text-zinc-500 mt-2">
                      User Agent: {error.user_agent || 'N/A'}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </Card>
    </div>
  );
}
